import { DatasourceTypeEnum } from "../enums/datasourceTypeEnum";
import { dataSourceItem } from "../../redux/models/reduxModels";
import { SharePointInputData } from "../../interfaces/props/SharePointInputData";

export type CustomFieldType = "text" | "datetime" | "number" | "currency" | "note";

export interface ICustomFieldDefinition {
  name: string;
  type: CustomFieldType;
  formats?: string[];
}

export const CUSTOM_FIELDS_INTERNAL_NAME = "DocubirdCustomFields";
export const CUSTOM_FIELDS_DISPLAY_NAME = "custom fields";

const SUPPORTED_TYPES: ReadonlyArray<string> = ["text", "datetime", "number", "currency", "note"];

/**
 * Internal-name first: a tenant may own a column merely NAMED "Custom fields" that holds
 * unrelated data - binding to it by display name alone would feed garbage to the save screen.
 * Display name stays as the fallback for admin-created columns.
 *
 * Type guard: only plain text columns qualify. A Lookup/User/Calculated column that happens
 * to carry the name would otherwise land in $select without $expand, SharePoint REST 400s
 * the whole items request, and every template in the library vanishes from the picker.
 * A field with no TypeAsString provided is accepted (older call sites don't select it).
 */
const isEnvelopeCapableType = (typeAsString?: string) =>
  typeAsString === undefined || typeAsString === "Note" || typeAsString === "Text";

export const resolveCustomFieldsColumnInternalName = (
  fields?: { InternalName?: string; Title?: string; TypeAsString?: string }[]
): string | undefined => {
  if (!Array.isArray(fields)) return undefined;
  const byInternal = fields.find(f => f?.InternalName === CUSTOM_FIELDS_INTERNAL_NAME && isEnvelopeCapableType(f?.TypeAsString));
  if (byInternal) return byInternal.InternalName;
  return fields.find(f => f?.Title?.toLowerCase().trim() === CUSTOM_FIELDS_DISPLAY_NAME && isEnvelopeCapableType(f?.TypeAsString))?.InternalName;
};

/**
 * Defensive, all-or-nothing envelope parse: one unrecognized entry means a future/foreign
 * envelope, and the whole template is treated as unscanned rather than half-rendered.
 */
const stripQuotedLiterals = (format: string) => format.replace(/'[^']*'/g, "").replace(/"[^"]*"/g, "");

/** True when any .NET date format carries time specifiers (H h m s t) outside quoted literals. */
export const datetimeHasTimeComponent = (formats?: string[]): boolean =>
  Array.isArray(formats) && formats.some(f => /[Hhmst]/.test(stripQuotedLiterals(f ?? "")));

const XML_TYPE_BY_CUSTOM_TYPE: Record<CustomFieldType, string> = {
  text: "Text",
  note: "Note",
  number: "Number",
  currency: "Currency",
  datetime: "DateTime",
};

/**
 * Synthesizes a SharePoint-field-style input so InputMetaDataFactory renders custom
 * placeholders with the exact validation/picker behaviour real columns get. Lives in a
 * SEPARATE state array - custom inputs must never enter inputValuesToSave (PII columns,
 * e-mail mappings, publish flows and SetItemFieldValues all iterate that list).
 */
export const customFieldToInputData = (def: ICustomFieldDefinition, index: number): SharePointInputData => {
  const xmlType = XML_TYPE_BY_CUSTOM_TYPE[def.type];
  const xml: any = {
    DisplayName: def.name,
    Type: xmlType,
    Required: "TRUE",
    ...(def.type === "datetime" ? { Format: datetimeHasTimeComponent(def.formats) ? "DateTime" : "DateOnly" } : {}),
  };
  return {
    index,
    internalName: `custom:${def.name}:${def.type}`,
    value: undefined,
    hasError: true, // required and empty until the user provides a value
    type: xmlType,
    displayName: def.name,
    xml,
    choices: undefined,
  };
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Local wall-time ISO, deliberately without a timezone suffix: what the user picked is what fills. */
export const toLocalIsoString = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

/**
 * The document shows exactly what you typed - except dates (ISO to the server, rendered by
 * each token's own format) and currency (formatted with the site's regional separators,
 * falling back to the raw string).
 */
export const serializeCustomFieldValue = (
  def: ICustomFieldDefinition,
  value: any,
  regionalSetting?: { DecimalSeparator?: string; ThousandSeparator?: string }
): string => {
  switch (def.type) {
    case "datetime":
      return value instanceof Date ? toLocalIsoString(value) : String(value ?? "");
    case "note":
      return String(value ?? "").replace(/\r\n?/g, "\n");
    case "currency": {
      const raw = String(value ?? "");
      const num = Number.parseFloat(raw);
      if (!regionalSetting?.DecimalSeparator || Number.isNaN(num)) return raw;
      const [intPart, decPart] = num.toFixed(2).split(".");
      const grouped = regionalSetting.ThousandSeparator
        ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, regionalSetting.ThousandSeparator)
        : intPart;
      return `${grouped}${regionalSetting.DecimalSeparator}${decPart}`;
    }
    case "number": {
      // Plain decimals pass through as typed with only the decimal separator aligned to the
      // site's regional settings - never routed through Number, which silently rewrites
      // digits beyond 2^53 (an 18-digit reference number must survive verbatim) and drops
      // deliberate leading/trailing zeros. No grouping: "2026" is more often a year than
      // two-thousand-and-twenty-six.
      const raw = String(value ?? "");
      const trimmed = raw.trim();
      if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(trimmed)) {
        return regionalSetting?.DecimalSeparator ? trimmed.replace(".", regionalSetting.DecimalSeparator) : trimmed;
      }
      // Exponent notation ("1e5"), which type="number" inputs accept: expand it only when
      // the expansion is provably exact - otherwise keep what was typed. Exact means: the
      // mantissa fits double precision (<= 15 significant digits), the expansion has no
      // exponent left, and an integer result is within the safe-integer range.
      if (/^[+-]?(\d+\.?\d*|\.\d+)[eE][+-]?\d+$/.test(trimmed)) {
        const significantDigits = trimmed.replace(/[eE][+-]?\d+$/, "").replace(/[^0-9]/g, "").replace(/^0+/, "").length;
        const num = Number(trimmed);
        const plain = num.toString();
        const exact = significantDigits <= 15 && Number.isFinite(num)
          && !plain.includes("e") && !plain.includes("E")
          && (plain.includes(".") || Number.isSafeInteger(num));
        if (exact) {
          return regionalSetting?.DecimalSeparator ? plain.replace(".", regionalSetting.DecimalSeparator) : plain;
        }
      }
      return raw;
    }
    default:
      return String(value ?? "");
  }
};

const normalizeUrl = (url?: string) => url?.toLowerCase().trim().replace(/\/+$/, "");

/**
 * True (returns the datasource) when the given location IS a template-datasource library:
 * same normalized site url and the same list title as a SharePointDocumentTemplate datasource.
 */
export const isTemplateDatasourceLocation = (
  datasources: dataSourceItem[] | undefined,
  currentWebUrl: string | undefined,
  currentLibraryTitle: string | undefined
): dataSourceItem | undefined => {
  if (!Array.isArray(datasources) || !currentWebUrl || !currentLibraryTitle) return undefined;
  const webUrl = normalizeUrl(currentWebUrl);
  const library = currentLibraryTitle.toLowerCase().trim();
  return datasources.find(ds =>
    ds?.type?.id === DatasourceTypeEnum.SharePointDocumentTemplate
    && normalizeUrl(ds.url) === webUrl
    && ds.name?.toLowerCase().trim() === library);
};

export const parseCustomFieldsEnvelope = (raw: unknown): ICustomFieldDefinition[] | undefined => {
  try {
    if (typeof raw !== "string" || !raw.trim()) return undefined;
    const envelope = JSON.parse(raw);
    if (envelope?.v !== 1 || !Array.isArray(envelope.fields)) return undefined;

    const parsed: ICustomFieldDefinition[] = [];
    const seenNames = new Set<string>();
    for (const field of envelope.fields) {
      if (typeof field?.name !== "string" || !field.name.trim()) return undefined;
      if (!SUPPORTED_TYPES.includes(field?.type)) return undefined;
      // formats must be an array of strings: a non-string element would reach
      // stripQuotedLiterals and throw INSIDE a React commit, white-screening the panel.
      if (field.formats !== undefined
        && (!Array.isArray(field.formats) || field.formats.some((f: unknown) => typeof f !== "string"))) {
        return undefined;
      }
      // The scan groups names case-insensitively, so a duplicate here means a foreign or
      // hand-edited envelope - same all-or-nothing treatment as any other malformation.
      const identity = field.name.trim().toLowerCase();
      if (seenNames.has(identity)) return undefined;
      seenNames.add(identity);
      parsed.push({
        name: field.name,
        type: field.type as CustomFieldType,
        formats: Array.isArray(field.formats) && field.formats.length > 0 ? field.formats : undefined,
      });
    }
    return parsed;
  } catch {
    return undefined;
  }
};
